import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type { ServiceOffering, ServiceConfiguration, ServiceConfigurationFE, PreviewResolutionResponse } from '../../../types/backend';

export interface ServiceOfferingCreateRequest {
  name: string;
  display_name: string;
  description?: string;
  owner?: string;
  category?: string;
  customer_description?: string;
  cloud_platforms?: string[];
  sla_tier?: string;
  requires_offerings?: string[];
}

export interface ServiceOfferingUpdateRequest {
  display_name?: string;
  description?: string;
  owner?: string;
  category?: string;
  customer_description?: string;
  cloud_platforms?: string[];
  sla_tier?: string;
  requires_offerings?: string[];
}

export interface PromptRef {
  target_context: string;
  asset_type: string;
  asset_id: string;
  role: 'enrichment' | 'automation';
}

export interface ServiceCapabilityCreateRequest {
  capability_id: string;
  name: string;
  display_name: string;
  description?: string;
  group?: string;
  rule_kind?: string | null;
  default_priority?: string | null;
  template_ref?: { target_context: string; asset_type: string; asset_id: string } | null;
  prompt_refs?: PromptRef[];
  automation_agent_id?: string;
  enablement_refs?: { provisioner_kind: string; ref: string }[];
  depends_on?: string[];
}

export interface ServiceCapabilityUpdateRequest {
  display_name?: string;
  description?: string;
  group?: string;
  rule_kind?: string | null;
  default_priority?: string | null;
  template_ref?: { target_context: string; asset_type: string; asset_id: string } | null;
  prompt_refs?: PromptRef[];
  automation_agent_id?: string;
  enablement_refs?: { provisioner_kind: string; ref: string }[];
  depends_on?: string[];
}

export interface ServiceOfferingListResponse {
  service_offerings: ServiceOffering[];
  total_count: number;
}

export interface SubscribedOfferingsListResponse {
  service_configurations: ServiceConfiguration[];
  total_count: number;
}

export interface CreateSubscriptionRequest {
  customer_id: string;
  offering_id: string;
  pinned_offering_version: string;
  enabled_capabilities?: string[];
}

export interface ToggleCapabilityRequest {
  customer_id: string;
  offering_id: string;
  capability_id: string;
  enabled: boolean;
}

export interface SetOperatorInputsRequest {
  customer_id: string;
  offering_id: string;
  component_name: string;
  inputs: Record<string, string>;
}

export interface EditCapabilityConfigOverrideRequest {
  customer_id: string;
  offering_id: string;
  capability_id: string;
  priority_override?: string | null;
  prompt_override?: { target_context: string; asset_type: string; asset_id: string; role?: string } | null;
}

export const createServiceOfferingsService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listOfferings: (): Promise<ServiceOfferingListResponse> =>
      rpcCall<ServiceOfferingListResponse>(client, 'service_offerings.ListServiceOfferings'),

    getOffering: (offering_id: string): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.GetServiceOffering', { offering_id }),

    createOffering: (request: ServiceOfferingCreateRequest): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.CreateServiceOffering', request as unknown as Record<string, unknown>),

    updateOffering: (offering_id: string, request: ServiceOfferingUpdateRequest): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.UpdateServiceOffering', { offering_id, ...request as unknown as Record<string, unknown> }),

    publishOffering: (offering_id: string, version: string): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.PublishServiceOffering', { offering_id, version }),

    deprecateOffering: (offering_id: string): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.DeprecateServiceOffering', { offering_id }),

    deleteOffering: (offering_id: string): Promise<void> =>
      rpcCall<void>(client, 'service_offerings.DeleteServiceOffering', { offering_id }),

    addCapability: (offering_id: string, request: ServiceCapabilityCreateRequest): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.AddCapability', { offering_id, ...request as unknown as Record<string, unknown> }),

    editCapabilityOverride: (offering_id: string, capability_id: string, request: ServiceCapabilityUpdateRequest): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.EditCapabilityOverride', { offering_id, capability_id, ...request as unknown as Record<string, unknown> }),

    removeCapability: (offering_id: string, capability_id: string): Promise<ServiceOffering> =>
      rpcCall(client, 'service_offerings.RemoveCapability', { offering_id, capability_id }),

    // Customer-scoped subscription methods
    listSubscribedOfferings: (customer_id: string): Promise<SubscribedOfferingsListResponse> =>
      rpcCall<SubscribedOfferingsListResponse>(client, 'service_offerings.ListSubscribedOfferings', { customer_id }),

    getSubscribedOffering: (customer_id: string, offering_id: string): Promise<ServiceConfiguration> =>
      rpcCall(client, 'service_offerings.GetSubscribedOffering', { customer_id, offering_id }),

    createSubscription: (request: CreateSubscriptionRequest): Promise<ServiceConfiguration> =>
      rpcCall(client, 'service_offerings.CreateSubscription', request as unknown as Record<string, unknown>),

    revokeSubscription: (customer_id: string, offering_id: string): Promise<void> =>
      rpcCall<void>(client, 'service_offerings.RevokeSubscription', { customer_id, offering_id }),

    toggleCapability: (request: ToggleCapabilityRequest): Promise<ServiceConfiguration> =>
      rpcCall(client, 'service_offerings.ToggleCapability', request as unknown as Record<string, unknown>),

    // Operator-only: per-customer capability override management
    editCapabilityConfigOverride: (request: EditCapabilityConfigOverrideRequest): Promise<ServiceConfiguration> =>
      rpcCall(client, 'service_offerings.EditCapabilityConfigOverride', request as unknown as Record<string, unknown>),

    removeCapabilityConfigOverride: (customer_id: string, offering_id: string, capability_id: string): Promise<ServiceConfiguration> =>
      rpcCall(client, 'service_offerings.RemoveCapabilityConfigOverride', { customer_id, offering_id, capability_id }),

    setOperatorInputs: (request: SetOperatorInputsRequest): Promise<ServiceConfigurationFE> =>
      rpcCall(client, 'service_offerings.SetOperatorInputs', request as unknown as Record<string, unknown>),

    previewResolution: (customer_id: string, offering_id: string, component_name: string): Promise<PreviewResolutionResponse> =>
      rpcCall<PreviewResolutionResponse>(client, 'service_offerings.PreviewResolution', { customer_id, offering_id, component_name }),
  };
};

export const useServiceOfferingsService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) throw new Error('useServiceOfferingsService requires authentication');
    return createServiceOfferingsService(idToken);
  }, [idToken]);
};
